import React from 'react';
import styled from 'styled-components';

import {
  Colors,
  DataStore,
  FontSizes,
  FontFamilies,
  MediaQueries,
  Image,
  Logger,
  Theme,
  isValidProviderResult
} from 'common';

import {
  Arrow,
  Button,
  Card,
  NumericSelect,
  Stepper,
  Text,
} from 'common/components';

import {
  withCommon,
} from 'common/hocs';

import {
  EventMobileTicketSummary
} from 'ui/event/components';

import {
  ActionContainer,
  Container,
  DetailContent,
  DetailContentText,
  FullWidthContainer,
  MainContentContainer,
  MobileContentSpace,
  SideContentContainer,
  SideContent,
  StepperContainer,
} from 'ui/event/styled';

import {
  EventProvider
} from 'providers';

const ActionContainerPopupOverlay = styled.div`
  background-color: ${Colors.Black}60;

  position: fixed;
  bottom: 65px;
  left: 0;
  top: 0;
  right: 0;

  ${props => !props.show && 'display: none;'}

  @media only screen and ${MediaQueries.Md} {
    display: none;
  }
`;

const ActionContainerPopup = styled.div`
  background-color: ${Theme.EventDetails.ActionContainerBackground};
  width: 100%;

  position: fixed;
  bottom: ${props => props.show ? '65px' : '-100%'};
  left: 0;

  transition: bottom 0.5s ease 0s;

  @media only screen and ${MediaQueries.Md} {
    display: none;
  }
`;

const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 15px 0 0;
`;

const PackageContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  width: 100%;

  @media only screen and ${MediaQueries.Md} {
    flex-direction: row;
  }
`;

const PackageImageContainer = styled.div`
  padding: 10px 0;

  @media only screen and ${MediaQueries.Md} {
    padding: 0;
    margin-right: 20px;
  }
`;

const PackageDetailsContainer = styled.div`
  flex: 1;
`;

const PriceAndSelectContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  width: 100%;
  justify-content: space-between;
`;

const IncludedItemsContainer = styled.div`
  margin-top: 15px;
  padding: 0 10px 10px 0;
  background-color: ${Colors.LightGray}30;
  border-radius: 8px;
`;

const IncludedItemRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckIcon = styled.span`
  color: ${Colors.Success};
  margin-right: 8px;
`;

const PackageVariantContainer = styled.div`
  background-color: ${Colors.LightGray}20;
  border-radius: 8px;
`;

const PackageVariantRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const PackageVariantLabel = styled.div`
  margin-bottom: 8px;
`;

const VariantButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const VariantButton = styled.button`
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid ${props => props.selected ? Colors.PrimaryOne : Colors.Gray};
  background-color: ${props => props.selected ? `${Colors.PrimaryOne}` : 'white'};
  color: ${props => props.selected ? Colors.White : Colors.Black};
  font-weight: ${props => props.selected ? 'bold' : 'normal'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    border-color: ${Colors.Primary};
    background-color: ${props => props.selected ? `${Colors.Primary}20` : `${Colors.Primary}10`};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: ${Colors.Gray};
    background-color: ${Colors.LightGray};
  }
`;

const PackageSelectUI = props => {
  const [state, setState] = React.useState({
    package: null,
    event: {},
    loading: true
  });

  const [totals, setTotals] = React.useState(DataStore.get('selectedTickets') || {
    selectedTickets: {},
    selectedMerch: {},
    selectedPackages: {},
    price: 0,
    tickets: 0,
  });

  const [showSummary, setShowSummary] = React.useState(false);

  const [packageVariants, setPackageVariants] = React.useState({});

  const onShowSummaryClick = () => {
    setShowSummary(true);
  };

  const onHideSummaryClick = () => {
    setShowSummary(false);
  };

  const onPackageCounterChange = (count, packageData) => {
    if (!packageData) return;

    const selectedPackages = {...totals.selectedPackages};
    const packageId = packageData.id || 'package-1';
    
    // Clear or update selected packages
    if (count === 0) {
      if (selectedPackages[packageId]) {
        delete selectedPackages[packageId];
      }
      // Clear variant selections for this package
      setPackageVariants({});
    } else {
      // If increasing count, maintain existing variants or initialize new ones
      const newVariants = {...packageVariants};

      // Remove excess variant selections if count decreased
      // The key structure is: ${packageId}-${itemId}-${index}
      Object.keys(newVariants).forEach(key => {
        if (key.startsWith(`${packageId}-`)) {
          const parts = key.split('-');
          if (parts.length >= 3) {
            const index = parseInt(parts[parts.length - 1]);
            if (index > count) {
              delete newVariants[key];
            }
          }
        }
      });
      
      setPackageVariants(newVariants);
      
      // Update selected packages - only store essential data
      // Preserve existing variants if they exist, but filter out indices beyond new count
      const existingVariants = selectedPackages[packageId]?.variants || {};
      const filteredVariants = {};
      
      // For each item, filter out variants for package indices beyond the new count
      Object.keys(existingVariants).forEach(itemId => {
        const itemVariants = existingVariants[itemId] || {};
        const filteredItemVariants = {};
        
        Object.keys(itemVariants).forEach(index => {
          if (parseInt(index) <= count) {
            filteredItemVariants[index] = itemVariants[index];
          }
        });
        
        if (Object.keys(filteredItemVariants).length > 0) {
          filteredVariants[itemId] = filteredItemVariants;
        }
      });
      
      selectedPackages[packageId] = {
        name: packageData.name,
        packageId: packageId,
        count: count,
        price: packageData.price,
        totalPrice: count * packageData.price,
        variants: filteredVariants
      };
    }

    let totalPrice = 0;
    let totalTickets = 0;

    // Calculate totals from tickets
    for (const item of Object.values(totals.selectedTickets)) {
      totalPrice += item.totalPrice;
      totalTickets += item.count;
    }

    // Calculate totals from merch
    for (const item of Object.values(totals.selectedMerch)) {
      totalPrice += item.totalPrice;
      totalTickets += item.count;
    }

    // Calculate totals from packages
    for (const item of Object.values(selectedPackages)) {
      totalPrice += item.totalPrice;
      totalTickets += item.count;
    }

    setTotals(prevState => ({
      ...prevState,
      selectedPackages: selectedPackages,
      price: totalPrice,
      tickets: totalTickets,
    }));

    setTimeout(() => {
      DataStore.set('selectedTickets', {
        selectedTickets: totals.selectedTickets,
        selectedMerch: totals.selectedMerch,
        selectedPackages: selectedPackages,
        price: totalPrice,
        tickets: totalTickets
      });
    }, 0);
  };

  const loadPackage = async (packageId) => {
    try {
      props.common.showLoading();
      
      // Get package data - now includes item_details nested in items
      const packageResponse = await EventProvider.getPackageById(packageId);
      
      if (!isValidProviderResult(packageResponse)) {
        throw new Error('Failed to load package data');
      }
      
      setState(prevState => ({
        ...prevState,
        package: packageResponse.data,
        loading: false
      }));
      
    } catch (error) {
      Logger.error('PackageSelect', 'loadPackage', error);
      props.common.showToast('Failed to load package details. Please try again.');
      props.common.navigate('/');
    } finally {
      props.common.hideLoading();
    }
  };

  const load = () => {
    if (!props.common?.location?.search) {
      Logger.error('PackageSelect', 'load', 'No search provided');
      props.common.showToast('Oh no, we weren\'t able to load the event details, please try again.');
      props.common.navigate('/');
      return;
    }

    const searchParams = new URLSearchParams(props.common.location.search);

    if (!searchParams.get('e_id')) {
      Logger.error('PackageSelect', 'load', 'No e_id provided');
      props.common.showToast('Oh no, we weren\'t able to load the event details, please try again.');
      props.common.navigate('/');
      return;
    }

    if (!searchParams.get('package_id')) {
      Logger.error('PackageSelect', 'load', 'No package_id provided');
      props.common.showToast('Oh no, we weren\'t able to load the package details, please try again.');
      props.common.navigate('/');
      return;
    }

    const eventId = searchParams.get('e_id');
    DataStore.set('selectedEvent', { id: eventId });
    
    loadPackage(searchParams.get('package_id'));
  };

  React.useEffect(load, []); // eslint-disable-line

  const renderIncludedItems = () => {
    if (!state.package || !state.package.items) return null;
    
    return (
      <IncludedItemsContainer>
        <Text fontFamily={FontFamilies.Bold} fontSize={FontSizes.Small} padding={'0 0 10px 0'}>
          Package Includes:
        </Text>
        
        {state.package.items.map((item, index) => {
          let itemName = '';
          
          if (item.item_type === 'product_id' && item.item_details) {
            itemName = item.item_details.product_name || 'Product';
          } else if (item.item_type === 'event_price_id' && item.item_details) {
            itemName = item.item_details.title || 'Ticket';
          }
          
          return (
            <IncludedItemRow key={`included-item-${index}`}>
              <CheckIcon>✓</CheckIcon>
              <Text fontSize={FontSizes.Small}>
                {itemName}
              </Text>
            </IncludedItemRow>
          );
        })}
      </IncludedItemsContainer>
    );
  };

  const renderProductVariants = () => {
    if (!state.package || !state.package.items) return null;
    
    // Find ALL product items with variants
    const productItemsWithVariants = state.package.items.filter(
      item => item.item_type === 'product_id' && 
              item.item_details && 
              item.item_details.variants && 
              item.item_details.variants.length > 0
    );
    
    if (productItemsWithVariants.length === 0) return null;
    
    const packageId = state.package.id || 'package-1';
    const packageCount = totals.selectedPackages[packageId]?.count || 0;
    
    if (packageCount === 0) return null;
    
    // Create an array of package indices
    const packageIndices = Array.from({length: packageCount}, (_, i) => i + 1);
    
    return (
      <>
        {productItemsWithVariants.map((productItem, itemIndex) => {
          const itemId = String(productItem.id || productItem.item_id || `item-${itemIndex}`);
          const variants = productItem.item_details.variants;
          
          return (
            <Card
              key={`variant-card-${itemId}`}
              backgroundColor={Theme.Card.Background}
              alignItems={'flex-start'}
              flexDirection={'column'}
              padding={'20px'}
              margin={'0 0 20px 0'}>
              
              <Text fontSize={FontSizes.Medium} fontFamily={FontFamilies.Bold} padding={'0 0 10px 0'}>
                {productItem.item_details?.product_name}
              </Text>
              
              <PackageVariantContainer>
                {packageIndices.map(index => {
                  const variantKey = `${packageId}-${itemId}-${index}`;
                  const selectedVariant = packageVariants[variantKey];
                  
                  return (
                    <PackageVariantRow key={variantKey}>
                      <PackageVariantLabel>
                        <Text fontFamily={FontFamilies.Bold}>
                          Package {index}:
                        </Text>
                      </PackageVariantLabel>
                      <VariantButtonsContainer>
                        {variants.map(variant => {
                          const isSelected = selectedVariant?.option_id === variant.option_id;
                          const outOfStock = variant.available_stock <= 0;
                          
                          return (
                            <VariantButton 
                              key={variant.option_id}
                              selected={isSelected}
                              disabled={outOfStock}
                              onClick={() => !outOfStock && onVariantSelect(itemId, index, variant.option_id)}
                            >
                              {variant.option_value}
                            </VariantButton>
                          );
                        })}
                      </VariantButtonsContainer>
                    </PackageVariantRow>
                  );
                })}
              </PackageVariantContainer>
            </Card>
          );
        })}
      </>
    );
  };
  
  const onVariantSelect = (itemId, packageIndex, variantId) => {
    const packageData = state.package;
    
    if (!packageData || !packageData.items) return;
    
    // Find the product item with variants by item ID
    // itemId can be a number or string, so we need to handle both
    const productItem = packageData.items.find(
      item => (String(item.id) === String(itemId) || String(item.item_id) === String(itemId)) &&
              item.item_type === 'product_id' && 
              item.item_details && 
              item.item_details.variants && 
              item.item_details.variants.length > 0
    );
    
    if (!productItem || !productItem.item_details) return;
    
    const variant = productItem.item_details.variants.find(v => v.option_id === variantId);
    if (!variant) return;
    
    const packageId = packageData.id || 'package-1';
    const variantKey = `${packageId}-${itemId}-${packageIndex}`;
    
    setPackageVariants(prev => ({
      ...prev,
      [variantKey]: variant
    }));
    
    // Update the totals with the variant information - only store essential variant data
    const selectedPackages = {...totals.selectedPackages};
    if (selectedPackages[packageId]) {
      // Only store minimal variant info needed for checkout
      const variantInfo = {
        option_id: variant.option_id,
        option_value: variant.option_value,
        product_variant_id: variant.product_variant_id
      };
      
      // Store variants per item: variants[itemId][packageIndex] = variantInfo
      // Ensure itemId is stored as string for consistency
      const itemIdStr = String(itemId);
      const itemVariants = selectedPackages[packageId].variants?.[itemIdStr] || {};
      
      selectedPackages[packageId] = {
        ...selectedPackages[packageId],
        variants: {
          ...selectedPackages[packageId].variants,
          [itemIdStr]: {
            ...itemVariants,
            [packageIndex]: variantInfo
          }
        }
      };
      
      setTotals(prev => ({
        ...prev,
        selectedPackages
      }));
      
      // Update DataStore
      setTimeout(() => {
        DataStore.set('selectedTickets', {
          ...totals,
          selectedPackages
        });
      }, 0);
    }
  };

  const areAllVariantsSelected = () => {
    // Check if there are any packages selected
    const packageId = state.package?.id || 'package-1';
    const packageCount = totals.selectedPackages[packageId]?.count || 0;
    
    if (packageCount === 0) {
      return false;
    }
    
    // Check if there are any product items with variants
    if (!state.package || !state.package.items) {
      return true; // No variants required
    }
    
    // Find ALL product items with variants
    const productItemsWithVariants = state.package.items.filter(
      item => item.item_type === 'product_id' && 
              item.item_details && 
              item.item_details.variants && 
              item.item_details.variants.length > 0
    );
    
    // If no products with variants, variants are not required
    if (productItemsWithVariants.length === 0) {
      return true;
    }
    
    // Check if all package indices have variants selected for ALL product items
    for (const productItem of productItemsWithVariants) {
      const itemIndex = productItemsWithVariants.indexOf(productItem);
      const itemId = String(productItem.id || productItem.item_id || `item-${itemIndex}`);
      
      for (let i = 1; i <= packageCount; i++) {
        const variantKey = `${packageId}-${itemId}-${i}`;
        if (!packageVariants[variantKey]) {
          return false;
        }
      }
    }
    
    return true;
  };

  if (state.loading) {
    return (
      <Container padding={'20px'}>
        <Text textAlign={'center'}>Loading package details...</Text>
      </Container>
    );
  }

  if (!state.package) {
    return (
      <Container padding={'20px'}>
        <Text textAlign={'center'}>No package available for this event.</Text>
      </Container>
    );
  }

  return (
    <>
      <Text
        maxWidth={'1100px'}
        margin={'0 auto'}
        width={'100%'}
        display={'block'} 
        padding={'20px 15px 0 15px'} 
        fontSize={FontSizes.Small}
        color={Theme.Text.Color}
      >
        <span style={{cursor: 'pointer'}} onClick={() => props.common.navigate('/')}>← View Upcoming Events</span>
      </Text>

      <Text display={'block'} padding={'0 15px 5px 15px'} textAlign={'center'} fontSize={FontSizes.Small}>{state.event.location_display}</Text>

      <Text display={'block'} padding={'0 15px 20px 15px'} textAlign={'center'} fontSize={FontSizes.Small}>{state.event.displayDate}</Text>

      <FullWidthContainer>
        <StepperContainer>
          <Stepper
            items={[{text: 'ORDER'}, {text: 'DETAILS'}, {text: 'PAYMENT'}]}
            selectedIndex={0}
          />
        </StepperContainer>
      </FullWidthContainer>

      <Container padding={'5px 15px 15px 15px'}>
        <MainContentContainer>
          <Card
            backgroundColor={Theme.Card.Background}
            alignItems={'flex-start'}
            flexDirection={'column'}
            padding={'20px'}
            margin={'0 0 20px 0'}>

            <Text fontSize={FontSizes.Big} fontFamily={FontFamilies.Bold} padding={'0 0 10px 0'}>
              {state.package.name}
            </Text>

            <Text fontSize={FontSizes.Small} padding={'0 0 15px 0'}>
              {state.package.description}
            </Text>

            <PackageContainer>
              <PackageImageContainer>
                <Image
                  src={state.package.image_url}
                  width={'300px'}
                  height={'auto'}
                  borderRadius={'8px'}
                />
              </PackageImageContainer>

              <PackageDetailsContainer>
                <PriceAndSelectContainer>
                  <Text fontSize={FontSizes.Normal} fontFamily={FontFamilies.Bold} padding={'5px 0'}>
                    R{state.package.price}
                  </Text>

                  <NumericSelect
                    defaultValue={Object.values(totals.selectedPackages).reduce((sum, pkg) => sum + pkg.count, 0)}
                    onCounterChange={(count) => onPackageCounterChange(count, state.package)}
                  />
                </PriceAndSelectContainer>

                {renderIncludedItems()}
              </PackageDetailsContainer>
            </PackageContainer>
          </Card>

          {renderProductVariants()}

          <MobileContentSpace height={'80px'} />

          <ActionContainerPopupOverlay
            show={showSummary}
            onClick={onHideSummaryClick}
          />

          <ActionContainerPopup show={showSummary}>
            <EventMobileTicketSummary
              totals={totals}
              onArrowClick={onHideSummaryClick}
              checkoutDisabled={!areAllVariantsSelected()}
            />
          </ActionContainerPopup>

          <ActionContainer>
            <ArrowContainer onClick={onShowSummaryClick}>
              <Arrow
                active={true}
                size={'10px'}
                direction={'up'}
              />
            </ArrowContainer>
            
            <DetailContent padding={'0'}>
              <DetailContentText fontSize={FontSizes.Small}>Price:</DetailContentText>
              <DetailContentText fontSize={FontSizes.Big} bold>{`R${totals.price}`}</DetailContentText>
            </DetailContent>

            <Button 
              disabled={!areAllVariantsSelected()}
              onClick={() => {
                if (totals.tickets === 0) {
                  props.common.showToast('Please add items before checkout');
                  return;
                }

                if (!areAllVariantsSelected()) {
                  props.common.showToast('Please select sizes for all packages');
                  return;
                }

                props.common.navigate('/event/ticket/identity')
              }}>Checkout</Button>
          </ActionContainer>
        </MainContentContainer>

        <SideContentContainer margin={'0 0 0 15px'}>
          <SideContent
            marginTop={'0px'}
            top={'90px'}
            sticky={true}>
            <EventMobileTicketSummary
              totals={totals}
              checkoutDisabled={!areAllVariantsSelected()}
              onCheckoutClick={() => {
                if (totals.tickets === 0) {
                  props.common.showToast('Please add items before checkout');
                  return;
                }

                if (!areAllVariantsSelected()) {
                  props.common.showToast('Please select sizes for all packages');
                  return;
                }
                
                props.common.navigate('/event/ticket/identity')
              }}
            />
          </SideContent>
        </SideContentContainer>
      </Container>
    </>
  );
};

export const PackageSelect = withCommon(PackageSelectUI);
